// «Повторить движение» в виджете генератора (владелец 13.10: «тот режим, который есть в Видеостудии, расширить на наш
// виджет в генераторе, но с удобным визуальным функционалом как у Трендов»). Это карточка-инструмент своего вида
// (card_kind 'motion'): GenerateToolMode рисует для неё свою раскладку — ролик с отрезком · кто повторит · результат.
// Нейросеть — на выбор (владелец 13.10: «не нужно зашивать на какую-то одну нейронку»): замена человека в ролике
// (P-Video Replace, Wan 2.2 Animate Replace), переделка ролика Seedance 2.5 по видео-референсу, Kling Motion Control.
// Входы: ссылка /generate?tool=motion и карточка «Популярных» с видом «Повторить движение» (галочка в админке).
import type { Model } from '../../../data/models'
import type { GenerationCard } from '../../../types/generationCard'
import { attachStyleCard, getAttachedStyleCard, type AttachedStyleCard } from '../../../api/attachedStyleCard'
import { isMotionControlModel, isSeedance25Model, isVideoReplaceModel } from '../utils/referenceLimits'
import { REPEAT_MAX_IMAGE, REPEAT_MAX_VIDEO, REPEAT_MIN_SECONDS, type RefVideo } from '../../../api/scene'
import type { ClipReq } from '../../scene/components/MotionZone'

export const MOTION_TOOL_SLUG = 'motion'
export const MOTION_TOOL_TITLE = 'Повторить движение'
/** Начало отрезка ближе к нулю — ролик уходит как есть: хвост до длины отрезка сервер режет сам (duration запроса). */
export const CUT_EPS = 0.05
/** Короче этого замена человека в ролике не берёт (services/video_replace_input.MIN_SECONDS). */
export const REPLACE_MIN_SECONDS = 2
/** Выбор нейросети режима помним на браузер: следующий заход открывается на той же. */
export const MOTION_MODEL_KEY = 'umnik.motion.model'

/** Как нейросеть работает с роликом: replace — человек в ролике заменяется, фон и камера те же (P-Video, Wan);
 *  edit — Seedance 2.5 переделывает ролик по видео-референсу (несколько людей, сцена может меняться);
 *  motion — Kling Motion Control: движение из ролика, внешность и фон с фото. */
export type MotionKind = 'replace' | 'edit' | 'motion'
/** «Где будет» для человека: video — как в ролике (replace, edit), photo — как на фото (motion). */
export type MotionWhere = 'video' | 'photo'

export function isMotionCard(card: { card_kind?: string } | null | undefined): boolean {
	return card?.card_kind === 'motion'
}

export function motionKind(model: Pick<Model, 'id' | 'backendModel'> | null | undefined): MotionKind | null {
	if (!model) return null
	if (isVideoReplaceModel(model)) return 'replace'
	if (isMotionControlModel(model)) return 'motion'
	if (isSeedance25Model(model)) return 'edit'
	return null
}

export function whereOf(kind: MotionKind | null | undefined): MotionWhere {
	return kind === 'motion' ? 'photo' : 'video'
}

/** Порядок в переключателе и нейросеть по умолчанию: переделка Seedance 2.5 первой (владелец 23.10: «по дефолту
 *  нужно сделать сиденс»), потом замена человека (P-Video проверен на танце 11.10, Wan следом), потом Kling Motion
 *  Control. Внутри ранга — порядок каталога. */
function rank(m: Model): number {
	const kind = motionKind(m)
	if (kind === 'edit') return 0
	const key = `${m.id} ${m.backendModel || ''}`.toLowerCase()
	if (key.includes('p-video-replace')) return 1
	if (key.includes('animate-replace')) return 2
	if (kind === 'replace') return 3
	return 4
}

/** Нейросети режима из каталога. Seedance 2.5 берём только с видео-референсом (переделка ролика). */
export function motionModels(models: Model[]): Model[] {
	return models
		.filter((m) => m.category === 'video' && motionKind(m) != null)
		.filter((m) => motionKind(m) !== 'edit' || !!m.supportsReferenceVideo || !!m.maxReferenceVideoSeconds)
		.map((m, i) => ({ m, i }))
		.sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i)
		.map((x) => x.m)
}

/** Нейросети режима для человека (владелец 23.10: «другие нейронки пока скрыть для юзеров, для staff оставить выбор;
 *  для юзера на тренде действует нейронка, которую выбрали в админке»): стафф — весь список (выбор в пикере бара);
 *  остальные — нейросеть пина (карточка тренда несёт выбор админа, вход по ссылке — Seedance 2.5), без пина —
 *  Seedance 2.5. Один-два элемента у юзера значат: пилюля бара без списка. */
export function motionModelsFor(models: Model[], who: { staff: boolean; pinnedModelId?: string | null }): Model[] {
	const all = motionModels(models)
	if (who.staff) return all
	if (who.pinnedModelId) return all.filter((m) => m.id === who.pinnedModelId)
	return all.filter((m) => motionKind(m) === 'edit')
}

export function readMotionModelId(): string | null {
	try {
		return localStorage.getItem(MOTION_MODEL_KEY)
	} catch {
		return null
	}
}

export function rememberMotionModelId(id: string): void {
	try {
		localStorage.setItem(MOTION_MODEL_KEY, id)
	} catch {
		/* без хранилища — не запомним */
	}
}

/** С какой нейросети открывать режим: заданная (карточка админа), иначе запомненная, иначе первая по рангу. */
export function defaultMotionModel(models: Model[], preferredId?: string | null, rememberedId: string | null = readMotionModelId()): Model | null {
	return (
		(preferredId ? models.find((m) => m.id === preferredId) : undefined) ??
		(rememberedId ? models.find((m) => m.id === rememberedId) : undefined) ??
		models[0] ??
		null
	)
}

/** Нейросети раскладки «Где будет»: как в ролике — replace и edit, как на фото — motion. */
export function modelsFor(models: Model[], where: MotionWhere): Model[] {
	return models.filter((m) => whereOf(motionKind(m)) === where)
}

/** Группы списка нейросетей в пилюле бара (владелец 19.10: «нейронка не должна меняться кнопкой „Где будет“, этих
 *  кнопок в теле не должно быть»): где окажется аватар — заголовок группы, под ним её нейросети. Одно решение в одном
 *  месте, в теле виджета только цепочка ролик → кто → результат. */
export const WHERE_GROUPS: { key: MotionWhere; label: string; hint: string }[] = [
	{ key: 'video', label: 'Как в ролике', hint: 'аватар встанет на место человека, фон и камера из ролика' },
	{ key: 'photo', label: 'Как на фото', hint: 'движение из ролика, внешность и фон с фото аватара' },
]

/** Нейросеть сама выбирает, кого брать: Kling Motion Control — того, кто занимает больше места в кадре (руководство
 *  Kling), Wan Animate Replace — тоже одного, промпта у неё нет (прогоны владельца). Наш выбор «кого» им не уходит, так
 *  что по людям у них активна только плитка самого заметного (сервер отдаёт людей по заметности, первый — самый
 *  крупный), остальные приглушены с объяснением (владелец 18.10: «опираться именно на условия нейронки»). */
export function autoTarget(model: Pick<Model, 'id' | 'backendModel' | 'maxImages'> | null | undefined): boolean {
	const kind = motionKind(model)
	return kind === 'motion' || (kind === 'replace' && maxWho(model) === 1)
}

/** Пределы отрезка у нейросети: замена человека 2–30 с, Seedance по лимитам референса (2–30 с), Kling Motion Control
 *  от 3 с и до 30 («по кадру» — 10). Те же числа сервер проверяет до списания. */
export function clipLimits(
	model: Pick<Model, 'id' | 'backendModel' | 'minReferenceVideoSeconds' | 'maxReferenceVideoSeconds'> | null | undefined,
	orientation?: string,
): { min: number; cap: number; replace: boolean } {
	const kind = motionKind(model)
	if (kind === 'motion') {
		return { min: REPEAT_MIN_SECONDS, cap: orientation === 'image' ? REPEAT_MAX_IMAGE : REPEAT_MAX_VIDEO, replace: false }
	}
	if (kind === 'edit') {
		return {
			min: Math.max(1, model?.minReferenceVideoSeconds ?? REPLACE_MIN_SECONDS),
			cap: Math.min(REPEAT_MAX_VIDEO, model?.maxReferenceVideoSeconds ?? REPEAT_MAX_VIDEO),
			replace: true,
		}
	}
	return { min: REPLACE_MIN_SECONDS, cap: REPEAT_MAX_VIDEO, replace: true }
}

/** Пин режима поверх виджета: без id и атрибуции (это не карточка витрины), toolMode включает GTM, вид 'motion' — его
 *  раскладку, целевая нейросеть — чтобы ручная смена модели снимала режим, как у любой карточки (useDetachOnModelMismatch). */
export function motionPin(
	model: Pick<Model, 'id' | 'provider'>,
	base?: Partial<Omit<AttachedStyleCard, 'attachedAt'>>,
): Omit<AttachedStyleCard, 'attachedAt'> {
	return {
		id: null,
		kind: 'video',
		title: MOTION_TOOL_TITLE,
		toolMode: true,
		card_kind: 'motion',
		targetAdapter: model.provider,
		targetModelId: model.id,
		...base,
	}
}

export function attachMotionTool(model: Pick<Model, 'id' | 'provider'>): void {
	attachStyleCard(motionPin(model))
}

/** Смена нейросети внутри режима штатным пикером виджета: пин следует за выбором (иначе useDetachOnModelMismatch снял бы
 *  карточку и режим), выбор запоминаем на браузер. Не карточка режима — ничего не делаем. */
export function repinMotionModel(m: Pick<Model, 'id' | 'provider'>): void {
	const pin = getAttachedStyleCard()
	if (!pin || !isMotionCard(pin) || pin.targetModelId === m.id) return
	const { attachedAt: _at, ...rest } = pin
	void _at
	attachStyleCard({ ...rest, targetAdapter: m.provider, targetModelId: m.id })
	rememberMotionModelId(m.id)
}

/** Карточка для applyCard: нейросеть режима, без промпта и превью. id с префиксом popular- — applyCard не
 *  перепривязывает пин (тот же гейт, что у карточек «Популярных»). */
export function motionToolCard(model: Pick<Model, 'id' | 'name'>): GenerationCard {
	return {
		id: `popular-${MOTION_TOOL_SLUG}`,
		type: 'generation',
		previewUrl: '',
		prompt: '',
		promptLocked: true,
		modelId: model.id,
		modelName: model.name,
		isVideo: true,
		kind: 'video',
		toolMode: true,
		card_kind: 'motion',
	}
}

/** Сколько людей в ролике нейросеть заменит за раз (владелец 13.10: «кнопка количества людей в видео, и столько слотов
 *  для аватаров… в ролике может быть и 10 человек»): предел — только лимит самой нейросети. Seedance — сколько фото
 *  референсов принимает (на проде 30), P-Video — до трёх фото (документация Pruna), Wan и Kling — один человек (при
 *  нескольких берут самого крупного). */
export const EDIT_MAX_WHO_FALLBACK = 9
export function maxWho(model: Pick<Model, 'id' | 'backendModel' | 'maxImages'> | null | undefined): number {
	const kind = motionKind(model)
	if (kind === 'edit') return Math.max(1, model?.maxImages ?? EDIT_MAX_WHO_FALLBACK)
	if (kind === 'replace' && `${model?.id} ${model?.backendModel || ''}`.toLowerCase().includes('p-video-replace')) return 3
	return 1
}

/** Кого заменить, когда аватар один, а людей в ролике может быть несколько: самого крупного (по умолчанию), по месту в
 *  кадре или всех. «Всех» умеет только переделка Seedance. */
export type ReplaceTarget = 'largest' | 'left' | 'center' | 'right' | 'all'
export const REPLACE_TARGETS: { key: ReplaceTarget; label: string; editOnly?: boolean }[] = [
	{ key: 'largest', label: 'Самого крупного' },
	{ key: 'left', label: 'Слева' },
	{ key: 'center', label: 'В центре' },
	{ key: 'right', label: 'Справа' },
	{ key: 'all', label: 'Всех', editOnly: true },
]

/** Нейросети, которым можно сказать, кого менять: переделка Seedance (промптом) и P-Video (instruction_prompt). */
export function targetChoosable(model: Pick<Model, 'id' | 'backendModel'> | null | undefined): boolean {
	const kind = motionKind(model)
	return kind === 'edit' || (kind === 'replace' && maxWho(model) > 1)
}

const TARGET_PHRASE: Record<ReplaceTarget, string> = {
	largest: 'the main performer, the largest person in the frame',
	left: 'the person on the left of the frame',
	center: 'the person in the center of the frame',
	right: 'the person on the right of the frame',
	all: 'every person',
}

/** Человек из ролика, на которого положили аватара (services/motion_people): описание, где стоит, когда появляется —
 *  так нейросеть находит его, а не считает «третий слева». Порядок — порядок фото в запросе. */
export interface MotionPair {
	desc: string
	position?: string | null
	firstT?: number | null
}

/** Описание в середину фразы: без точки в конце, с маленькой буквы (сервер уже так отдаёт, это страховка). */
function pairDesc(p: MotionPair): string {
	const t = p.desc.trim().replace(/[.;,!]+$/, '').trim()
	return t.length >= 2 && /[A-Z]/.test(t[0]) && /[a-z]/.test(t[1]) ? t[0].toLowerCase() + t.slice(1) : t
}

function pairWhere(p: MotionPair): string {
	const parts: string[] = []
	if (p.position === 'center') parts.push('in the center of the frame')
	else if (p.position === 'left' || p.position === 'right') parts.push(`on the ${p.position} of the frame`)
	if (p.firstT != null && p.firstT >= 1.5) parts.push(`appearing at about ${Math.round(p.firstT)}s`)
	return parts.length ? ` (${parts.join(', ')})` : ''
}

/** Промпт режима под капотом (человеку не показываем): у Seedance — полный промпт переделки с @Video1/@ImageN, у P-Video —
 *  подсказка «кто кого» (instruction_prompt). Люди из ролика описаны словами (people, 14.10: порядковые номера Seedance
 *  путал — дубли и пропуски у четырёх аватаров); без описаний — по местам слева направо, как стоят в ролике; один —
 *  выбранная цель, остальные не трогаются. Wan и Kling текст не читают — пусто. */
export function buildMotionPrompt(p: { kind: MotionKind | null; count: number; target?: ReplaceTarget; people?: MotionPair[] }): string {
	const count = Math.max(1, Math.floor(p.count || 1))
	const target = p.target ?? 'largest'
	const people = p.people?.filter((x) => x.desc.trim()) ?? []
	if (p.kind === 'edit') {
		const base =
			'Use @Video1 as the base: keep its choreography, motion, camera movement, timing, lighting and background exactly as ' +
			'they are. '
		const tail = ' Keep the original audio.'
		if (people.length) {
			const pairs = people.map((x, i) => `${pairDesc(x)}${pairWhere(x)} becomes the person from @Image${i + 1}`).join('; ')
			const order = people.length > 1 ? ' The reference images are listed in the order these people first appear in the video.' : ''
			return `${base}Replace exactly ${people.length === 1 ? 'one person' : `${people.length} people`}: ${pairs}.${order} Each replacement keeps the face, hair, body type and clothes of its reference image. Keep everyone else exactly as they are.${tail}`
		}
		if (count === 1) {
			if (target === 'all') return `${base}Replace every person in the video with the person from @Image1: same face, hair, body type and clothes as in @Image1.${tail}`
			return `${base}Replace exactly one person, ${TARGET_PHRASE[target]}, with the person from @Image1: same face, hair, body type and clothes as in @Image1. Keep every other person exactly as they are.${tail}`
		}
		const pairs = Array.from({ length: count }, (_, i) => `person ${i + 1} becomes the person from @Image${i + 1}`).join(', ')
		return `${base}There are ${count} people to replace. Count them from left to right as they stand in the frame: ${pairs}. Each keeps the face, hair, body type and clothes of their reference image. Keep anyone else exactly as they are.${tail}`
	}
	if (p.kind === 'replace') {
		if (people.length) {
			const pairs = people.map((x, i) => `${pairDesc(x)}${pairWhere(x)} with the person from image ${i + 1}`).join('; ')
			return `Replace ${pairs}. Keep everyone else unchanged.`
		}
		if (count === 1) {
			if (target === 'all') return 'Replace every person in the video with the person from the reference image.'
			return `Replace only ${TARGET_PHRASE[target]} with the person from the reference image. Keep everyone else unchanged.`
		}
		const pairs = Array.from({ length: count }, (_, i) => `person ${i + 1} becomes the person from image ${i + 1}`).join(', ')
		return `Count the people from left to right as they stand in the frame: ${pairs}. Keep anyone else unchanged.`
	}
	return ''
}

/** Ролик из слота виджета → отрезок режима: с начала, длиной в весь ролик (не больше предела нейросети). */
export function clipFromUpload(
	v: { url?: string; duration?: number; file?: { name?: string } | null } | null | undefined,
	cap: number,
): RefVideo | null {
	if (!v?.url) return null
	const seconds = Number(v.duration) > 0 ? Number(v.duration) : null
	return {
		url: v.url,
		name: v.file?.name || undefined,
		seconds,
		start: 0,
		length: seconds ? wholeClipLength(seconds, cap) : undefined,
	}
}

/** Отрезок на весь ролик: целые секунды, не больше предела нейросети. */
export function wholeClipLength(seconds: number, cap: number): number {
	return Math.max(1, Math.min(cap, Math.floor(seconds + 0.05)))
}

/** Секунды референса, как их округлит сервер Seedance для списания (seedance_refs.reference_seconds_for_billing:
 *  вверх с допуском 0,25 с): 12,37 с — 13, 12,2 с — 12. */
export const BILL_SLACK = 0.25
export function billedSeconds(seconds: number): number {
	return Math.max(1, Math.ceil(seconds - BILL_SLACK))
}

/** Резать ли ролик на сервере до запуска. Замена и Kling: только когда начало не в нуле — хвост до длины отрезка сервер
 *  режет сам. Seedance хвост не режет и списывает по длине референса, округлённой вверх: целый ролик 12,37 с ушёл бы как
 *  13 с при 12 с на кнопке (владелец 14.10: списано 2015 вместо 1860) — режем всегда, когда ролик спишется не как отрезок. */
export function clipNeedsCut(clip: Pick<RefVideo, 'start' | 'seconds'> | null | undefined, length?: number, kind: MotionKind = 'replace'): boolean {
	if (!clip) return false
	if ((Number(clip.start) || 0) >= CUT_EPS) return true
	if (kind !== 'edit' || !length) return false
	const seconds = Number(clip.seconds)
	return seconds > 0 && billedSeconds(seconds) !== length
}

/** Ключ отрезка — тот же отрезок второй раз не режем (повторный запуск с теми же ручками). */
export function clipSegmentKey(clip: Pick<RefVideo, 'url' | 'start'>, length: number): string {
	return `${clip.url}|${Math.round((Number(clip.start) || 0) * 10) / 10}|${length}`
}

/** Сводка пилюли настроек у нейросетей, где формат и длину задаёт ролик: «Как у видео · 720p · 8 с · Звук». Секунды —
 *  только когда ролик уже в слоте (до него длина отрезка не известна). */
export function clipSummaryParts(p: { resolution?: string | null; seconds?: number | null; sound?: boolean }): string[] {
	const parts = ['Как у видео']
	if (p.resolution) parts.push(p.resolution)
	if (p.seconds && p.seconds > 0) parts.push(`${p.seconds} с`)
	if (p.sound) parts.push('Звук')
	return parts
}

/** Ставка за секунду итога для подписи в переключателе: посекундные строки модели (P-Video, Wan, Kling MC) или строки
 *  «<разрешение>_video» Seedance; самая низкая. Нет ни тех, ни других — «от» модели. */
export function perSecondPrice(m: Pick<Model, 'cost' | 'variants'>): number {
	const vs = m.variants ?? []
	const perSec = vs.filter((v) => v.is_per_second && v.credits_price != null).map((v) => v.credits_price as number)
	const video = vs.filter((v) => /_video$/.test(v.key) && v.credits_price != null).map((v) => v.credits_price as number)
	const pool = perSec.length ? perSec : video
	const rate = pool.length ? Math.min(...pool) : m.cost
	return Math.round(rate * 10) / 10
}

/** Слова и условия хорошего ролика до загрузки — по выбранной нейросети (владелец 13.10: одни правила на всех врут про
 *  число людей и строгость). Источники: отказы Wan и Kling в проде (services/error_messages), советы Pruna по P-Video
 *  (свет, лицо спереди), лимиты референса Seedance. */
export function clipHints(
	model: Pick<Model, 'id' | 'backendModel' | 'maxImages'> | null | undefined,
	limits: { min: number; cap: number },
): { lead: string; reqs: ClipReq[] } {
	const kind = motionKind(model)
	const people = maxWho(model)
	const sec: ClipReq = { icon: 'timer', text: `${limits.min}–${limits.cap} с` }
	if (kind === 'edit') {
		return {
			lead: 'Танец, жест или сцена — Seedance переделает ролик с вашими людьми, движение и камера останутся. Подойдёт, когда:',
			reqs: [{ icon: 'users', text: 'людей сколько угодно — по слотам' }, { icon: 'cut', text: 'лучше одним куском' }, sec],
		}
	}
	if (kind === 'motion') {
		return {
			// Kling переносит только непрерывный кусок и иначе режет итог до 3 с — условия решают и длину ролика.
			lead: 'Танец или жест — аватар с фото повторит его в своей обстановке. Ролик выйдет на всю длину, когда:',
			reqs: [{ icon: 'user', text: 'один человек' }, { icon: 'standing', text: 'по пояс или целиком' }, { icon: 'camera', text: 'камера стоит' }, { icon: 'cut', text: 'без склеек' }, sec],
		}
	}
	if (people > 1) {
		return {
			lead: 'Танец или жест — аватар встанет на место человека, фон, камера и звук останутся. Лучше всего получается, когда:',
			reqs: [{ icon: 'users', text: `до ${people} человек` }, { icon: 'face', text: 'лицо видно' }, { icon: 'light', text: 'хороший свет' }, sec],
		}
	}
	return {
		lead: 'Танец или жест — аватар встанет на место человека, фон, свет и звук останутся. Лучше всего получается, когда:',
		reqs: [{ icon: 'user', text: 'один человек' }, { icon: 'face', text: 'лицо видно всё время' }, { icon: 'frame', text: 'не выходит из кадра' }, { icon: 'light', text: 'хороший свет' }, sec],
	}
}
